import { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Space,
  Typography,
  Tag,
} from 'antd';
import { PlusOutlined, EditOutlined, UploadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  useSuppliers,
  useSupplierById,
  useCreateSupplier,
  useUpdateSupplier,
} from '@hooks/usePurchasing';
import { useImportSuppliers } from '@hooks/useImport';
import type {
  SupplierListItem,
  SupplierDetail,
  SupplierFilters,
  CreateSupplierRequest,
} from '@/types/purchasing';
import ImportModal from '@modules/common/ImportModal';

/** Значення форми з картки постачальника (null з сервера → undefined для полів форми) */
function mdToFormValues(mdSupplier: SupplierDetail): CreateSupplierRequest {
  return {
    code: mdSupplier.code,
    name: mdSupplier.name,
    legalName: mdSupplier.legalName ?? undefined,
    country: mdSupplier.country ?? undefined,
    taxId: mdSupplier.taxId ?? undefined,
    vatNumber: mdSupplier.vatNumber ?? undefined,
    email: mdSupplier.email ?? undefined,
    phone: mdSupplier.phone ?? undefined,
    contactPerson: mdSupplier.contactPerson ?? undefined,
    website: mdSupplier.website ?? undefined,
    paymentTerms: mdSupplier.paymentTerms ?? undefined,
    paymentTermDays: mdSupplier.paymentTermDays,
    notes: mdSupplier.notes ?? undefined,
    street: mdSupplier.street ?? undefined,
    city: mdSupplier.city ?? undefined,
    region: mdSupplier.region ?? undefined,
    postalCode: mdSupplier.postalCode ?? undefined,
  };
}

export default function SuppliersPage() {
  const [mdIsModalOpen, setMdIsModalOpen] = useState(false);
  const [mdEditingSupplierId, setMdEditingSupplierId] = useState<string | null>(null);
  const [mdSearch, setMdSearch] = useState('');
  const [mdPage, setMdPage] = useState(1);
  const [mdPageSize, setMdPageSize] = useState(20);
  const [mdForm] = Form.useForm<CreateSupplierRequest>();

  const mdFilters: SupplierFilters = {
    search: mdSearch || undefined,
    page: mdPage,
    pageSize: mdPageSize,
  };

  const [mdIsImportModalOpen, setMdIsImportModalOpen] = useState(false);
  const { data: mdSuppliersData, isLoading: mdIsLoading } = useSuppliers(mdFilters);
  const mdCreateSupplier = useCreateSupplier();
  const mdUpdateSupplier = useUpdateSupplier();
  const mdImportSuppliers = useImportSuppliers();

  // SupplierDto списку не містить більшості полів форми — редагуємо за карткою GET /suppliers/{id}
  const { data: mdSupplierDetail } = useSupplierById(mdEditingSupplierId ?? undefined);
  const mdEditingSupplier =
    mdSupplierDetail && mdSupplierDetail.id === mdEditingSupplierId ? mdSupplierDetail : undefined;
  const mdIsDetailLoading = !!mdEditingSupplierId && !mdEditingSupplier;

  useEffect(() => {
    if (mdIsModalOpen && mdEditingSupplier) {
      mdForm.setFieldsValue(mdToFormValues(mdEditingSupplier));
    }
  }, [mdIsModalOpen, mdEditingSupplier, mdForm]);

  const mdHandleOpenCreate = () => {
    setMdEditingSupplierId(null);
    mdForm.resetFields();
    mdForm.setFieldsValue({ paymentTermDays: 30 });
    setMdIsModalOpen(true);
  };

  const mdHandleOpenEdit = (mdSupplier: SupplierListItem) => {
    mdForm.resetFields();
    setMdEditingSupplierId(mdSupplier.id);
    setMdIsModalOpen(true);
  };

  const mdHandleSubmit = async () => {
    const mdValues = await mdForm.validateFields();
    if (mdEditingSupplierId) {
      if (!mdEditingSupplier) return;
      await mdUpdateSupplier.mutateAsync({
        ...mdValues,
        id: mdEditingSupplierId,
        // UpdateSupplierCommand без isActive активує постачальника — передаємо поточний стан
        isActive: mdEditingSupplier.isActive,
      });
    } else {
      await mdCreateSupplier.mutateAsync(mdValues);
    }
    setMdIsModalOpen(false);
    mdForm.resetFields();
  };

  const mdColumns: ColumnsType<SupplierListItem> = [
    {
      title: 'Код',
      dataIndex: 'code',
      key: 'code',
      width: 100,
      sorter: (mdA, mdB) => mdA.code.localeCompare(mdB.code),
    },
    {
      title: 'Назва',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      sorter: (mdA, mdB) => mdA.name.localeCompare(mdB.name),
    },
    {
      title: 'Країна',
      dataIndex: 'country',
      key: 'country',
      width: 120,
    },
    {
      title: 'ЄДРПОУ',
      dataIndex: 'taxId',
      key: 'taxId',
      width: 120,
      render: (mdValue?: string | null) => mdValue || '—',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 200,
      ellipsis: true,
    },
    {
      title: 'Телефон',
      dataIndex: 'phone',
      key: 'phone',
      width: 150,
    },
    {
      title: 'Статус',
      key: 'isActive',
      width: 100,
      render: (_: unknown, mdRecord: SupplierListItem) =>
        mdRecord.isActive ? (
          <Tag color="green">Активний</Tag>
        ) : (
          <Tag color="default">Неактивний</Tag>
        ),
    },
    {
      title: 'Дії',
      key: 'actions',
      width: 80,
      render: (_: unknown, mdRecord: SupplierListItem) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={(mdEvent) => {
            mdEvent.stopPropagation();
            mdHandleOpenEdit(mdRecord);
          }}
        />
      ),
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Постачальники
        </Typography.Title>
        <Space>
          <Button
            icon={<UploadOutlined />}
            onClick={() => setMdIsImportModalOpen(true)}
          >
            Імпорт з Excel
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={mdHandleOpenCreate}>
            Додати постачальника
          </Button>
        </Space>
      </div>

      <Space style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Пошук за назвою, кодом, ЄДРПОУ..."
          allowClear
          onSearch={(mdValue) => {
            setMdSearch(mdValue);
            setMdPage(1);
          }}
          style={{ width: 350 }}
        />
      </Space>

      <Table<SupplierListItem>
        columns={mdColumns}
        dataSource={mdSuppliersData?.items}
        rowKey="id"
        loading={mdIsLoading}
        pagination={{
          current: mdPage,
          pageSize: mdPageSize,
          total: mdSuppliersData?.totalCount,
          showSizeChanger: true,
          showTotal: (mdTotal) => `Всього: ${mdTotal}`,
          onChange: (mdNewPage, mdNewPageSize) => {
            setMdPage(mdNewPage);
            setMdPageSize(mdNewPageSize);
          },
        }}
        size="middle"
      />

      <Modal
        title={mdEditingSupplierId ? 'Редагувати постачальника' : 'Новий постачальник'}
        open={mdIsModalOpen}
        onOk={mdHandleSubmit}
        onCancel={() => setMdIsModalOpen(false)}
        confirmLoading={mdCreateSupplier.isPending || mdUpdateSupplier.isPending}
        okButtonProps={{ disabled: mdIsDetailLoading }}
        okText="Зберегти"
        cancelText="Скасувати"
        width={640}
      >
        <Form form={mdForm} layout="vertical" style={{ marginTop: 16 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            <Form.Item
              name="code"
              label="Код"
              rules={[{ required: true, message: 'Введіть код' }]}
            >
              <Input maxLength={20} disabled={!!mdEditingSupplierId} />
            </Form.Item>
            <Form.Item
              name="name"
              label="Назва"
              rules={[{ required: true, message: 'Введіть назву' }]}
            >
              <Input maxLength={200} />
            </Form.Item>
          </div>
          <Form.Item name="legalName" label="Юридична назва">
            <Input maxLength={300} />
          </Form.Item>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 16px' }}>
            <Form.Item name="country" label="Країна">
              <Input maxLength={100} />
            </Form.Item>
            <Form.Item name="taxId" label="ЄДРПОУ">
              <Input maxLength={20} />
            </Form.Item>
            <Form.Item name="vatNumber" label="ІПН (платника ПДВ)">
              <Input maxLength={20} />
            </Form.Item>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            <Form.Item name="email" label="Email">
              <Input type="email" maxLength={200} />
            </Form.Item>
            <Form.Item name="phone" label="Телефон">
              <Input maxLength={30} />
            </Form.Item>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            <Form.Item name="contactPerson" label="Контактна особа">
              <Input maxLength={200} />
            </Form.Item>
            <Form.Item name="website" label="Вебсайт">
              <Input maxLength={200} />
            </Form.Item>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            <Form.Item name="paymentTerms" label="Умови оплати">
              <Input maxLength={100} />
            </Form.Item>
            <Form.Item
              name="paymentTermDays"
              label="Термін оплати (днів)"
              rules={[{ required: true, message: 'Введіть термін' }]}
            >
              <InputNumber min={0} max={365} style={{ width: '100%' }} />
            </Form.Item>
          </div>
          <Typography.Text strong>Адреса</Typography.Text>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '0 16px', marginTop: 8 }}>
            <Form.Item name="street" label="Вулиця">
              <Input maxLength={300} />
            </Form.Item>
            <Form.Item name="city" label="Місто">
              <Input maxLength={100} />
            </Form.Item>
            <Form.Item name="region" label="Область">
              <Input maxLength={100} />
            </Form.Item>
            <Form.Item name="postalCode" label="Індекс">
              <Input maxLength={10} />
            </Form.Item>
          </div>
          <Form.Item name="notes" label="Примітки">
            <Input.TextArea rows={3} maxLength={2000} />
          </Form.Item>
        </Form>
      </Modal>

      <ImportModal
        open={mdIsImportModalOpen}
        title="Імпорт постачальників з Excel"
        onClose={() => setMdIsImportModalOpen(false)}
        onImport={(mdFile) => mdImportSuppliers.mutateAsync(mdFile)}
        isPending={mdImportSuppliers.isPending}
      />
    </>
  );
}
